import { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getAllProducts as getLocalProducts } from '../lib/dataStore';

var ProductsContext = createContext({});

// Only these fields are ever sent to the database
var COLUMNS = [
  'name', 'slug', 'category', 'description', 'materials', 'dimensions',
  'base_price', 'compare_price', 'stock', 'low_stock_threshold', 'status',
  'sort_order', 'variants', 'colours', 'images',
  'sku', 'tags', 'etsy_listing_id'
];

function normalise(row) {
  return Object.assign({}, row, {
    base_price: Number(row.base_price),
    compare_price: row.compare_price === null ? null : Number(row.compare_price),
    variants: row.variants || [],
    colours: row.colours || [],
    images: row.images || [],
    sku: row.sku || '',
    tags: row.tags || [],
    etsy_listing_id: row.etsy_listing_id || null
  });
}

function sortProducts(list) {
  return list.slice().sort(function(a, b) { return a.sort_order - b.sort_order; });
}

// Plain-English messages for Andre
function friendlyError(error) {
  if (!error) return null;
  var text = [error.message, error.details].join(' ');
  if (error.code === '23505' && text.indexOf('etsy_listing_id') >= 0) return 'Another product was already imported from this Etsy listing.';
  if (error.code === '23505') return 'Another product already uses this name. Please choose a different name.';
  // The SKU/tags/Etsy columns (step 7 migration) are not in the database yet
  if (error.code === '42703' || error.code === 'PGRST204') return 'The database is missing the newest product fields (SKU, tags, Etsy). Please apply the latest database update, then try again.';
  if (error.code === '23514') return 'A price or stock number is not valid. Numbers cannot be negative.';
  return 'Could not save. Please check your internet connection and try again.';
}

async function fetchProducts() {
  if (!supabase) {
    return { products: sortProducts(getLocalProducts()), error: 'Not connected to the database' };
  }
  var result = await supabase.from('products').select('*').order('sort_order');
  if (result.error) {
    console.error('Could not load products from Supabase:', result.error.message);
    return { products: sortProducts(getLocalProducts()), error: result.error.message };
  }
  return { products: sortProducts(result.data.map(normalise)), error: null };
}

export function ProductsProvider({ children }) {
  var [allProducts, setAllProducts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState(null);

  useEffect(function() {
    var cancelled = false;
    fetchProducts().then(function(res) {
      if (cancelled) return;
      setAllProducts(res.products);
      setLoadError(res.error);
      setLoading(false);
    });
    return function() { cancelled = true; };
  }, []);

  function reload() {
    return fetchProducts().then(function(res) {
      setAllProducts(res.products);
      setLoadError(res.error);
    });
  }

  var products = allProducts.filter(function(p) { return p.status === 'live'; });

  function getProductBySlug(slug) {
    return allProducts.find(function(p) { return p.slug === slug; }) || null;
  }

  function getProductById(id) {
    return allProducts.find(function(p) { return p.id === id; }) || null;
  }

  async function saveProduct(product) {
    if (!supabase || loadError) {
      return { data: null, error: 'Not connected to the database. Changes were not saved.' };
    }
    var row = {};
    COLUMNS.forEach(function(col) {
      if (product[col] !== undefined) row[col] = product[col];
    });
    row.updated_at = new Date().toISOString();

    var result = product.id
      ? await supabase.from('products').update(row).eq('id', product.id).select().single()
      : await supabase.from('products').insert(row).select().single();

    if (result.error) {
      console.error('Save failed:', result.error);
      return { data: null, error: friendlyError(result.error) };
    }
    await reload();
    return { data: normalise(result.data), error: null };
  }

  async function deleteProduct(id) {
    if (!supabase || loadError) {
      return { error: 'Not connected to the database. Nothing was deleted.' };
    }
    var result = await supabase.from('products').delete().eq('id', id);
    if (result.error) {
      console.error('Delete failed:', result.error);
      return { error: 'Could not delete. Please check your internet connection and try again.' };
    }
    await reload();
    return { error: null };
  }

  return (
    <ProductsContext.Provider value={{
      products: products,
      allProducts: allProducts,
      loading: loading,
      loadError: loadError,
      getProductBySlug: getProductBySlug,
      getProductById: getProductById,
      saveProduct: saveProduct,
      deleteProduct: deleteProduct,
      reload: reload
    }}>
      {children}
    </ProductsContext.Provider>
  );
}

export function useProducts() {
  return useContext(ProductsContext);
}